import { AFTER_SALES_STEP_TYPES, COMMERCE_BACKGROUND_STEP_TYPES, COMMERCE_FACING_STEP_TYPES, SERVICE_BACKGROUND_STEP_TYPES, SERVICE_FACING_STEP_TYPES } from "@sagot/constants";
import type { Step } from "../types";
import type { BuilderRefs } from "../builder/refs-context";
import { ASSIGN_PICKS_IN_TEAM, DEFAULT_LINK_VARIABLE, type ReplyAuthor } from "./service-constants";
import { checkOrderIncomplete, lookupCatalogueIncomplete, summarizeOrders } from "./orders-model";
import type { ServiceRefs, ServiceTypeOption } from "./service-refs";
import type {
  AssignTicketStep,
  CreateTicketStep,
  NotifyContactStep,
  ServiceStep,
  TicketFacingStep,
  UpdateTicketStep,
} from "./service-types";

/** What `t` of `useTranslation` is to these functions (tests pass a plain function). */
type TFunction = (key: string, options?: Record<string, unknown>) => string;

const S = "engage.workflows.service.summaries";
const UNKNOWN = "engage.workflows.summaries.unknown";

const BACKGROUND: ReadonlySet<string> = new Set([...SERVICE_BACKGROUND_STEP_TYPES, ...AFTER_SALES_STEP_TYPES, ...COMMERCE_BACKGROUND_STEP_TYPES]);
const FACING: ReadonlySet<string> = new Set([...SERVICE_FACING_STEP_TYPES, ...COMMERCE_FACING_STEP_TYPES]);

/** Whether a step type belongs to the Service Desk: the steps that work behind the scenes (tickets, assets and visits, the catalogue and orders) and the six the client sees. */
export const isServiceType = (type: string): boolean => BACKGROUND.has(type) || FACING.has(type);
export const isServiceStep = (step: Step): step is ServiceStep => isServiceType(step.type);
/** The Service Desk steps the client sees and answers: the three ticket steps and the three order steps. */
export const isTicketFacingStep = (step: Step): step is TicketFacingStep => FACING.has(step.type);

/** A new step with the choices a person starts from; the id comes from `freshStepId`. Anything left empty is flagged on the card. */
export function newServiceStep(type: ServiceStep["type"], id: string): ServiceStep {
  switch (type) {
    case "create_ticket":
      return { id, type, title: "", description: "", notify_client: true };
    case "update_ticket":
      return { id, type };
    case "set_ticket_state":
      return { id, type, status: "" };
    case "route_ticket":
      return { id, type, mode: "workspace_rules" };
    case "assign_ticket":
      return { id, type, method: "round_robin" };
    case "add_ticket_note":
      return { id, type, text: "" };
    case "reply_to_ticket":
      return { id, type, text: "", author: "system" };
    case "notify_contact":
      return { id, type, to: ["requester"], channels: ["email"], template: "ticket_status" };
    case "link_ticket":
      return { id, type, source: "variable", variable: DEFAULT_LINK_VARIABLE };
    case "apply_ticket_sla":
      return { id, type, policy_id: "" };
    case "request_csat":
    case "ticket_intake":
    case "show_ticket_form":
    case "show_my_tickets":
    case "create_order":
    case "show_order_status":
      return { id, type };
    case "lookup_asset":
      return { id, type, source: "event" };
    case "schedule_visit":
      return { id, type, kind: "corrective" };
    case "lookup_catalogue":
      return { id, type, source: "query", query: "" };
    case "check_order_status":
      return { id, type, source: "event" };
    case "show_catalogue":
      return { id, type, query: "" };
  }
}

/** The ticket type a step names, by id or by the key of a seeded type. */
export function typeOf(step: { type_id?: string; type_key?: string }, service: ServiceRefs | undefined): ServiceTypeOption | undefined {
  const types = service?.types ?? [];
  if (step.type_id) return types.find((type) => type.id === step.type_id);
  return step.type_key ? types.find((type) => type.key === step.type_key) : undefined;
}

const hasType = (step: { type_id?: string; type_key?: string }): boolean => Boolean(step.type_id ?? step.type_key);

function typeName(step: { type_id?: string; type_key?: string }, refs: BuilderRefs, t: TFunction): string {
  if (!hasType(step)) return t(`${S}.anyType`);
  return typeOf(step, refs.service)?.name ?? step.type_key ?? t(UNKNOWN);
}

function nameIn(options: readonly { id: string; name: string }[] | undefined, id: string | undefined, t: TFunction): string {
  return options?.find((option) => option.id === id)?.name ?? t(UNKNOWN);
}

function list(items: readonly string[]): string {
  return items.join(", ");
}

/** What `update_ticket` changes, in a few words ("priority high, add tags vip, due in 2 days"). */
function updateParts(step: UpdateTicketStep, refs: BuilderRefs, t: TFunction): string[] {
  const parts: string[] = [];
  if (hasType(step)) parts.push(t(`${S}.updateType`, { type: typeName(step, refs, t) }));
  if (step.priority) parts.push(t(`${S}.updatePriority`, { priority: t(`engage.workflows.service.priorities.${step.priority}`) }));
  if (step.tags_add?.length) parts.push(t(`${S}.updateTagsAdd`, { tags: list(step.tags_add) }));
  if (step.tags_remove?.length) parts.push(t(`${S}.updateTagsRemove`, { tags: list(step.tags_remove) }));
  const fields = Object.keys(step.fields ?? {});
  if (fields.length > 0) parts.push(t(`${S}.updateFields`, { count: fields.length }));
  if (step.due_in_minutes) parts.push(t(`${S}.updateDue`, { count: step.due_in_minutes }));
  if (step.clear_due) parts.push(t(`${S}.updateClearDue`));
  return parts;
}

function assignSummary(step: AssignTicketStep, refs: BuilderRefs, t: TFunction): string {
  const team = nameIn(refs.teams, step.team_id, t);
  switch (step.method) {
    case "round_robin":
    case "least_open":
      return t(`${S}.assign_${step.method}`, { team });
    case "skills_match":
      return t(`${S}.assign_skills_match`, { team, skills: list(step.required_skills ?? []) || t(UNKNOWN) });
    case "user":
      return t(`${S}.assign_user`, { name: nameIn(refs.members, step.user_id, t) });
    case "unassign":
      return t(`${S}.assign_unassign`);
  }
}

function notifySummary(step: NotifyContactStep, t: TFunction): string {
  const who = list(step.to.map((audience) => t(`engage.workflows.service.fields.audiences.${audience}`)));
  const channels = list((step.channels ?? ["email"]).map((channel) => t(`engage.workflows.service.fields.channelNames.${channel}`)));
  return t(`${S}.notify_contact`, { who: who || t(UNKNOWN), channels, template: t(`engage.workflows.service.fields.templates.${step.template}`) });
}

const authorName = (author: ReplyAuthor | undefined, user: string, t: TFunction): string => (author === "user" ? user : t("engage.workflows.service.fields.authors.system"));

/** One line that says what a Service Desk step does, shown on its card. */
export function summarizeService(step: ServiceStep, refs: BuilderRefs, t: TFunction): string {
  switch (step.type) {
    case "create_ticket":
      return t(hasType(step) ? `${S}.create_ticket` : `${S}.create_ticket_any`, { type: typeName(step, refs, t), title: step.title || t(UNKNOWN) });
    case "update_ticket":
      return updateParts(step, refs, t).join(" · ") || t(UNKNOWN);
    case "set_ticket_state":
      return t(`${S}.set_ticket_state`, { status: step.status ? nameIn(refs.service?.statuses, step.status, t) : t(UNKNOWN) });
    case "route_ticket":
      return t(`${S}.route_ticket`);
    case "assign_ticket":
      return assignSummary(step, refs, t);
    case "add_ticket_note":
      return t(`${S}.add_ticket_note`, { text: step.text || t(UNKNOWN) });
    case "reply_to_ticket":
      return t(`${S}.reply_to_ticket`, { author: authorName(step.author, nameIn(refs.members, step.user_id, t), t), text: step.text || t(UNKNOWN) });
    case "notify_contact":
      return notifySummary(step, t);
    case "link_ticket":
      return t(`${S}.link_ticket`, { variable: step.variable ?? t(UNKNOWN) });
    case "apply_ticket_sla":
      return t(`${S}.apply_ticket_sla`, { policy: nameIn(refs.service?.policies, step.policy_id, t) });
    case "request_csat":
      return t(`${S}.request_csat`);
    case "ticket_intake":
      return t(hasType(step) ? `${S}.ticket_intake_typed` : `${S}.ticket_intake`, { type: typeName(step, refs, t) });
    case "show_ticket_form":
      return t(`${S}.show_ticket_form`, { type: typeName(step, refs, t) });
    case "show_my_tickets":
      return t(`${S}.show_my_tickets`);
    default:
      return summarizeOrders(step, t);
  }
}

const skillsMissing = (step: AssignTicketStep): boolean => step.method === "skills_match" && (step.required_skills ?? []).length === 0;

/** What `assign_ticket` still needs: the team to pick from, the person, or the skills (the `builder.incomplete` key), or `null`. */
function assignIncomplete(step: AssignTicketStep): string | null {
  if (ASSIGN_PICKS_IN_TEAM.has(step.method) && !step.team_id) return "assignTeam";
  if (step.method === "user" && !step.user_id) return "assignUser";
  return skillsMissing(step) ? "assignSkills" : null;
}

function createIncomplete(step: CreateTicketStep): string | null {
  if (!hasType(step)) return "ticketType";
  return step.title.trim() ? null : "ticketTitle";
}

/** Whether a tag is in both lists of `update_ticket` (the server refuses a tag that is added and removed). */
export function tagsClash(step: Pick<UpdateTicketStep, "tags_add" | "tags_remove">): boolean {
  const removed = new Set((step.tags_remove ?? []).map((tag) => tag.toLowerCase()));
  return (step.tags_add ?? []).some((tag) => removed.has(tag.toLowerCase()));
}

function updateIncomplete(step: UpdateTicketStep): string | null {
  const listed = (list: readonly string[] | undefined): boolean => (list?.length ?? 0) > 0;
  const changes = hasType(step) || step.priority !== undefined || listed(step.tags_add) || listed(step.tags_remove) || Object.keys(step.fields ?? {}).length > 0 || step.due_in_minutes !== undefined || step.clear_due === true;
  if (!changes) return "updateNothing";
  return tagsClash(step) ? "tagsClash" : null;
}

/** What a Service Desk step still needs before it can work (the `builder.incomplete` key), or `null`. Only a hint on the card: the server checks the saved draft. */
export function serviceIncompleteOf(step: ServiceStep): string | null {
  switch (step.type) {
    case "create_ticket":
      return createIncomplete(step);
    case "update_ticket":
      return updateIncomplete(step);
    case "set_ticket_state":
      return step.status ? null : "ticketStatus";
    case "assign_ticket":
      return assignIncomplete(step);
    case "add_ticket_note":
      return step.text.trim() ? null : "noteText";
    case "reply_to_ticket":
      if (!step.text.trim()) return "replyText";
      return step.author === "user" && !step.user_id ? "replyAuthor" : null;
    case "notify_contact":
      if (step.to.length === 0) return "notifyAudience";
      return (step.channels ?? []).length === 0 ? "notifyChannel" : null;
    case "link_ticket":
      return step.source === "variable" && !step.variable ? "linkVariable" : null;
    case "apply_ticket_sla":
      return step.policy_id ? null : "ticketPolicy";
    case "show_ticket_form":
      return hasType(step) ? null : "ticketType";
    case "route_ticket":
    case "request_csat":
    case "ticket_intake":
    case "show_my_tickets":
    case "create_order":
    case "show_order_status":
    case "schedule_visit":
    case "show_catalogue":
      return null;
    case "check_order_status":
      return checkOrderIncomplete(step);
    case "lookup_asset":
      return step.source === "variable" && !step.variable ? "assetVariable" : null;
    case "lookup_catalogue":
      return lookupCatalogueIncomplete(step);
  }
}
